<template>
  <SessionSlide
    ref="slide"
    :id="'slide' + props.index"
    visual-position="fixed"
    :class="{
      'slide-after-activity': !isDisplayed,
      'submitted-mapping': isDisplayed,
    }"
    :data-test="`session-slide-${props.index}`"
  >
    <template #text>
      <SlideText :text="text" :data-test="`${headType}-head-slice-text`" />
    </template>
    <template #visual>
      <VisualPositioningCanvas>
        <div :id="`${headType}-head-slice-${props.index}`" class="head-container">
          <HeadFromModel
            ref="visual"
            :headId="id"
            :whiteBackground="true"
            :kind="headType"
            :show-tooltips="true"
            :animate-entrance="false"
            @onDataLoaded="headLoaded"
            :containerForTooltips="`#${headType}-head-slice-${props.index}`"
            :data-test="`${headType}-head`"
          />
        </div>
      </VisualPositioningCanvas>
    </template>
  </SessionSlide>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { debug as d } from "debug";
import SessionSlide from "../../sessions/session-slide.vue";
import HeadFromModel from "../../head/head-from-model.vue";
import VisualPositioningCanvas from "../../sessions/visual-positioning-canvas.vue";
import { PrismicSliceWithData } from "../../../views/cms/cms-session.vue";
import {
  CmsRichText,
  CmsSessionSlideWithTextAndHead,
} from "../../../services/cms/cms-type-definitions";
import { csContext } from "../../../services/cs-context";
import { useCmsSessionStore } from "../../../stores/useCmsSessionStore";
import pinia from "../../../stores";
import SlideText from "./slide-text.vue";
import { useToggle } from "../../../composables/useToggle";

const debug = d("cs.SoloHeadSlice");

const props = defineProps<{
  slice: PrismicSliceWithData;
  index: number;
}>();

const cmsSessionStore = useCmsSessionStore(pinia());

const { state: isDisplayed, toggleState: toggleIsDisplayed } = useToggle(false);

const isHeadLoaded = ref(false);

const prismicSlice = computed<CmsSessionSlideWithTextAndHead>(() => {
  return props.slice.prismicSlice as CmsSessionSlideWithTextAndHead;
});

const headType = computed(() => prismicSlice.value.primary?.head_type ?? "solo");

const id = computed(() => {
  switch (headType.value) {
    case "teamsolo":
    case "teamviewpoints":
      return csContext.currentTeam?.id;
    case "viewpoints":
    case "solo":
    default:
      return csContext.user.id;
  }
});

const noSoloText = computed(() => {
  switch (headType.value) {
    case "teamsolo":
      return "Your team needs at least two members with Solo reports before you can see this.";
    case "teamviewpoints":
      return "Your team needs at least two members with Viewpoints reports before you can see this.";
    case "viewpoints":
      return "You need at least two Viewpoints reports before you can see this.";
    case "solo":
    default:
      return "You need to complete your strengths mapping in the Foundations Session before you can see this.";
  }
});

const text = computed(() => {
  if (isHeadLoaded.value) {
    return prismicSlice.value.primary.text_body;
  }

  return noSoloText.value as unknown as CmsRichText;
});

const headLoaded = (hasData: boolean): void => {
  debug("headLoaded", headType.value, hasData);
  isHeadLoaded.value = hasData;
};

// These 3 refs need to be exposed so that cms-session can apply animations to them
const visual = ref<InstanceType<typeof HeadFromModel> | null>(null);
const slide = ref<InstanceType<typeof SessionSlide> | null>(null);
const visualDiv = computed(() => slide?.value?.visualDiv);

// once the strengths mapping has finished -> get the data for the head
watchEffect(() => {
  if (cmsSessionStore.hasFinishedMapping.strengths) {
    visual.value?.getHeadModelData();
  }
});

watchEffect(() => {
  if (!isDisplayed.value && cmsSessionStore.sectionsToDisplay.includes(props.slice.section)) {
    toggleIsDisplayed();
  }
});

defineExpose({
  visualDiv,
  visual,
  slide,
});
</script>

<style scoped lang="less">
@import "../../../styles/sessions/cms-session";

.head-container {
  width: 50%;
  margin: auto;
  padding: 25% 0 25%;
}
</style>
